"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.aiOpenApiDocs = void 0;
// Standard error response schema with error_code support
const ErrorResponse = {
    type: 'object',
    properties: {
        success: {
            type: 'boolean',
            example: false
        },
        error: {
            type: 'string',
            description: 'Human-readable error message'
        },
        error_code: {
            type: 'integer',
            description: 'Numeric error code for programmatic handling'
        }
    },
    required: ['success', 'error']
};
const FrontChatMessageSchema = {
    type: 'object',
    description: 'Individual chat message supporting both text and multimodal content (text + images)',
    properties: {
        content: {
            oneOf: [
                {
                    type: 'string',
                    description: 'Simple text message content'
                },
                {
                    type: 'array',
                    description: 'Multimodal content array containing text and/or image elements',
                    items: {
                        type: 'object',
                        description: 'Content element - can be text or image',
                        properties: {
                            type: {
                                type: 'string',
                                enum: ['text', 'image'],
                                description: 'Type of content element'
                            },
                            text: {
                                type: 'string',
                                description: 'Text content (required when type is "text")'
                            },
                            image: {
                                type: 'string',
                                description: 'Base64 encoded image data or image URL (required when type is "image")'
                            }
                        },
                        required: ['type']
                    }
                }
            ],
            description: 'Message content - either a simple string or an array of content objects for multimodal messages (text + images)'
        },
        role: {
            type: 'string',
            enum: ['user', 'assistant', 'system'],
            description: 'Message sender role: "user" (human input), "assistant" (AI response), or "system" (system instructions)'
        },
        context: {
            type: 'object',
            description: 'Optional metadata and context information for the message',
            properties: {
                thoughts: {
                    type: 'string',
                    description: 'AI reasoning or thought process behind the message'
                },
                data_points: {
                    type: 'array',
                    items: { type: 'string' },
                    description: 'Relevant data points or facts used to generate the message'
                },
                citations: {
                    type: 'array',
                    description: 'Source citations for information used in the message',
                    items: {
                        type: 'object',
                        properties: {
                            text: {
                                type: 'string',
                                description: 'Citation text or title'
                            },
                            url: {
                                type: 'string',
                                description: 'Source URL (optional)'
                            }
                        },
                        required: ['text']
                    }
                }
            }
        },
        files: {
            type: 'array',
            description: 'Attached files associated with this message',
            items: {
                type: 'object',
                description: 'File attachment metadata',
                properties: {
                    id: {
                        type: 'number',
                        description: 'Unique file identifier'
                    },
                    name: {
                        type: 'string',
                        description: 'Original filename'
                    },
                    url: {
                        type: 'string',
                        description: 'File access URL'
                    },
                    type: {
                        type: 'string',
                        description: 'MIME type of the file'
                    }
                }
            }
        }
    },
    required: ['content', 'role']
};
const SelectedModelSchema = {
    type: 'object',
    description: 'AI model selection configuration including provider and model details',
    properties: {
        provider: {
            type: 'object',
            description: 'AI provider information',
            properties: {
                name: {
                    type: 'string',
                    description: 'Human-readable provider name (e.g., "OpenRouter", "OpenAI")'
                },
                value: {
                    type: 'string',
                    description: 'Provider identifier used by the system (e.g., "openrouter", "openai")'
                }
            },
            required: ['name', 'value']
        },
        model: {
            type: 'object',
            description: 'Specific AI model configuration',
            properties: {
                label: {
                    type: 'string',
                    description: 'Human-readable model name for display purposes',
                    example: 'GPT-4 Turbo'
                },
                value: {
                    type: 'string',
                    description: 'Model identifier used for API calls',
                    example: 'openai/gpt-4-1106-preview'
                }
            },
            required: ['label', 'value']
        },
        aiProvider: {
            type: 'string',
            enum: ['openrouter', 'together', 'satellite'],
            description: 'AI service provider identifier (should match provider.value)'
        },
        cost: {
            type: 'number',
            description: 'Estimated cost per 1K tokens for this model',
            minimum: 0
        }
    },
    required: ['provider', 'model']
};
const ChatPayloadSchema = {
    type: 'object',
    description: 'Complete chat request payload including messages, configuration, and streaming preferences',
    properties: {
        messages: {
            type: 'array',
            items: FrontChatMessageSchema,
            description: 'Array of chat messages. Each message contains content (text or multimodal), role (user/assistant/system), and optional context/files. This represents the conversation history and current user input.',
            minItems: 1
        },
        context: {
            type: 'object',
            description: 'Configuration object containing all chat behavior settings and preferences',
            properties: {
                approach: {
                    type: 'string',
                    enum: ['rrr'],
                    description: 'Chat processing approach. Currently only "rrr" (Retrieve, Reason, Respond) is supported for knowledge-enhanced responses.'
                },
                selectedModel: {
                    ...SelectedModelSchema,
                    description: 'AI model configuration including provider details, model selection, and cost information. Required for non-avatar chats.'
                },
                avatar: {
                    type: 'string',
                    description: 'Avatar ID for personality-driven conversations. When specified, the avatar\'s configured model and personality will be used.'
                },
                aiProvider: {
                    type: 'string',
                    enum: ['openrouter', 'together', 'satellite'],
                    description: 'AI service provider to use for processing the request. Defaults to "openrouter" if not specified.'
                },
                language: {
                    type: 'string',
                    description: 'ISO language code for response language (e.g., "en", "es", "fr"). If not specified, defaults to English or avatar\'s configured language.',
                    example: 'en'
                },
                knowledge: {
                    type: 'array',
                    items: { type: 'string' },
                    description: 'Array of knowledge base IDs to use for RAG (Retrieval Augmented Generation). These knowledge bases will be searched for relevant context.'
                },
                conversationId: {
                    type: 'string',
                    description: 'Existing conversation ID to continue a previous chat session. If not provided, a new conversation will be created.'
                },
                fullscan: {
                    type: 'boolean',
                    description: 'Enable comprehensive knowledge search across all available sources. May increase response time but provides more thorough results.',
                    default: false
                },
                webSearchEnabled: {
                    type: 'boolean',
                    description: 'Enable real-time web search for current information. Requires appropriate permissions and may incur additional costs.',
                    default: false
                },
                deepSearchEnabled: {
                    type: 'boolean',
                    description: 'Enable advanced search algorithms for more accurate knowledge retrieval. Recommended for complex queries.',
                    default: false
                },
                chatSettings: {
                    type: 'object',
                    description: 'Advanced chat behavior settings including learning preferences, conversation memory, and processing options.',
                    properties: {
                        chatLearning: {
                            type: 'boolean',
                            description: 'Enable AI learning from conversation for personalized responses'
                        },
                        temperature: {
                            type: 'number',
                            description: 'Response creativity level (0.0-1.0). Lower values = more focused, higher = more creative',
                            minimum: 0.0,
                            maximum: 1.0
                        },
                        maxTokens: {
                            type: 'integer',
                            description: 'Maximum number of tokens in the response',
                            minimum: 1,
                            maximum: 8192
                        }
                    }
                },
                writingStyle: {
                    type: 'array',
                    items: { type: 'string' },
                    description: 'Array of writing style IDs to apply to the response (e.g., "professional", "casual", "technical"). Styles can be combined for nuanced output.'
                },
                promptSlots: {
                    type: 'object',
                    description: 'Dynamic content slots for advanced prompt customization. Contains key-value pairs where keys are slot names and values are the content to inject.',
                    additionalProperties: { type: 'string' },
                    example: {
                        "target_audience": "software developers",
                        "tone": "technical but friendly"
                    }
                }
            },
            required: ['approach']
        },
        stream: {
            type: 'boolean',
            description: 'Whether to stream the response in real-time chunks (true) or return the complete response at once (false). Streaming is recommended for better user experience with long responses.',
            default: false
        }
    },
    required: ['messages', 'context']
};
exports.aiOpenApiDocs = {
    'ai.chat': {
        description: 'Generate AI chat response (supports streaming)',
        payload: {
            body: ChatPayloadSchema
        },
        responses: {
            200: {
                description: 'Chat response - streaming format when stream=true, JSON when stream=false',
                content: {
                    'text/plain': {
                        description: 'Streaming response (when stream=true)',
                        schema: {
                            type: 'string',
                            description: 'Streaming response with multiple JSON objects separated by newlines. Format: conversationId, requestId, streaming choices, and final cost'
                        },
                        examples: {
                            streaming: {
                                value: `{"conversationId":609}
{"requestId":"9abe5d34-9ae9-4136-86a2-32db69cf6c22"}
{"tool":{"name":"rag","data":{"fileList":[{"name":"Microsoft_Azure_ResourceHealth_ai.monster_1766420180636.pdf","id":261,"size":17217,"sources":["Source text from RAG asset"]}]}}}
{"choices":[{"delta":{"content":"Ah","role":"assistant"}}]}
{"choices":[{"delta":{"content":", te","role":"assistant"}}]}
{"choices":[{"delta":{"content":"raz kum","role":"assistant"}}]}
{"choices":[{"delta":{"content":"am","role":"assistant"}}]}
{"choices":[{"delta":{"content":"!","role":"assistant"}}]}
{"cost":{"prompt_tokens":3095,"completion_tokens":449,"total_tokens":3544,"cost":0.01602,"reasoning_tokens":0,"cached_tokens":0}}`
                            }
                        }
                    },
                    'application/json': {
                        description: 'JSON response (when stream=false)',
                        schema: {
                            type: 'object',
                            properties: {
                                success: { type: 'boolean' },
                                data: {
                                    type: 'object',
                                    properties: {
                                        message: { type: 'string' },
                                        conversationId: { type: 'string' },
                                        requestId: { type: 'string' },
                                        cost: {
                                            type: 'object',
                                            properties: {
                                                prompt_tokens: { type: 'number' },
                                                completion_tokens: { type: 'number' },
                                                total_tokens: { type: 'number' },
                                                cost: { type: 'number' },
                                                reasoning_tokens: { type: 'number' },
                                                cached_tokens: { type: 'number' }
                                            }
                                        }
                                    }
                                }
                            },
                            required: ['success', 'data']
                        }
                    }
                }
            },
            400: {
                description: 'Bad request - validation error',
                content: {
                    'application/json': {
                        schema: ErrorResponse
                    }
                }
            },
            500: {
                description: 'Internal server error',
                content: {
                    'application/json': {
                        schema: ErrorResponse
                    }
                }
            }
        }
    },
    'ai.generate-image': {
        description: 'Generate AI images using various models and pipelines',
        payload: {
            body: {
                type: 'object',
                description: 'Image generation request parameters',
                properties: {
                    prompt: {
                        type: 'string',
                        description: 'Text prompt describing the desired image. Be descriptive and specific for best results.',
                        example: 'A serene mountain landscape at sunset with a crystal clear lake reflection'
                    },
                    model: {
                        type: 'string',
                        description: 'AI model to use for image generation. If not specified, defaults to "gemini-nano-banana-2-1".',
                        example: 'gemini-nano-banana-2-1',
                        default: 'gemini-nano-banana-2-1'
                    },
                    pipeline: {
                        type: 'string',
                        enum: ['text-to-image', 'image-to-image'],
                        description: 'Image generation pipeline type. "text-to-image" generates from text prompts, "image-to-image" modifies existing images.',
                        default: 'text-to-image'
                    }
                },
                required: ['prompt']
            }
        },
        responses: {
            200: {
                description: 'Image generation successful',
                content: {
                    'application/json': {
                        schema: {
                            type: 'object',
                            properties: {
                                success: { type: 'boolean' },
                                data: {
                                    type: 'object',
                                    properties: {
                                        imageUrl: { type: 'string' },
                                        id: { type: 'number' },
                                        metadata: { type: 'object' }
                                    }
                                }
                            },
                            required: ['success', 'data']
                        }
                    }
                }
            },
            400: {
                description: 'Bad request - prompt is required',
                content: {
                    'application/json': {
                        schema: {
                            type: 'object',
                            properties: {
                                success: { type: 'boolean' },
                                error: { type: 'string' }
                            },
                            required: ['success', 'error']
                        }
                    }
                }
            },
            500: {
                description: 'Image generation failed',
                content: {
                    'application/json': {
                        schema: {
                            type: 'object',
                            properties: {
                                success: { type: 'boolean' },
                                error: { type: 'string' }
                            },
                            required: ['success', 'error']
                        }
                    }
                }
            }
        }
    },
    'ai.cancel-request': {
        description: 'Cancel an active AI request (chat, generation, etc.)',
        payload: {
            body: {
                type: 'object',
                description: 'Request cancellation parameters',
                properties: {
                    requestId: {
                        type: 'string',
                        description: 'Unique identifier of the request to cancel. This ID is returned when starting streaming requests.',
                        example: '9abe5d34-9ae9-4136-86a2-32db69cf6c22'
                    }
                },
                required: ['requestId']
            }
        },
        responses: {
            200: {
                description: 'Request cancellation result',
                content: {
                    'application/json': {
                        schema: {
                            type: 'object',
                            properties: {
                                success: { type: 'boolean' },
                                message: { type: 'string' }
                            },
                            required: ['success', 'message']
                        }
                    }
                }
            },
            400: {
                description: 'Bad request - requestId is required',
                content: {
                    'application/json': {
                        schema: {
                            type: 'object',
                            properties: {
                                success: { type: 'boolean' },
                                error: { type: 'string' }
                            },
                            required: ['success', 'error']
                        }
                    }
                }
            },
            404: {
                description: 'Request not found or already completed',
                content: {
                    'application/json': {
                        schema: {
                            type: 'object',
                            properties: {
                                success: { type: 'boolean' },
                                error: { type: 'string' }
                            },
                            required: ['success', 'error']
                        }
                    }
                }
            },
            500: {
                description: 'Internal server error',
                content: {
                    'application/json': {
                        schema: {
                            type: 'object',
                            properties: {
                                success: { type: 'boolean' },
                                error: { type: 'string' }
                            },
                            required: ['success', 'error']
                        }
                    }
                }
            }
        }
    },
    'ai.assist': {
        description: 'AI-powered prompt assistance and knowledge suggestions',
        payload: {
            body: {
                type: 'object',
                description: 'Prompt analysis and assistance request',
                properties: {
                    text: {
                        type: 'string',
                        description: 'Text prompt or query to analyze for improvements and knowledge suggestions. Should be at least a few words long.',
                        example: 'How can I improve my JavaScript code performance?'
                    },
                    language: {
                        type: 'string',
                        description: 'ISO language code for analysis and suggestions (e.g., "en", "es", "fr"). Defaults to English if not specified.',
                        example: 'en',
                        default: 'en'
                    },
                    context: {
                        type: 'object',
                        description: 'Additional context for the analysis',
                        properties: {
                            domain: {
                                type: 'string',
                                description: 'Domain or field context (e.g., "programming", "marketing", "science")'
                            },
                            audience: {
                                type: 'string',
                                description: 'Target audience level (e.g., "beginner", "intermediate", "expert")'
                            }
                        }
                    },
                    excludeKnowledgeIds: {
                        type: 'array',
                        items: { type: 'string' },
                        description: 'Array of knowledge base IDs to exclude from suggestions'
                    }
                },
                required: ['text']
            }
        },
        responses: {
            200: {
                description: 'Prompt analysis and suggestions',
                content: {
                    'application/json': {
                        schema: {
                            type: 'object',
                            properties: {
                                success: { type: 'boolean' },
                                data: {
                                    type: 'object',
                                    properties: {
                                        suggestions: {
                                            type: 'array',
                                            items: { type: 'string' }
                                        },
                                        analysis: { type: 'object' },
                                        overallScore: { type: 'number' },
                                        knowledgeResults: {
                                            type: 'array',
                                            items: { type: 'object' }
                                        },
                                        keywords: {
                                            type: 'array',
                                            items: { type: 'string' }
                                        },
                                        searchTerms: {
                                            type: 'array',
                                            items: { type: 'string' }
                                        },
                                        keywordConfidence: { type: 'number' }
                                    }
                                }
                            },
                            required: ['success', 'data']
                        }
                    }
                }
            },
            400: {
                description: 'Bad request - invalid input text',
                content: {
                    'application/json': {
                        schema: {
                            type: 'object',
                            properties: {
                                success: { type: 'boolean' },
                                error: { type: 'string' }
                            },
                            required: ['success', 'error']
                        }
                    }
                }
            },
            402: {
                description: 'Payment required - prompt assist feature not available',
                content: {
                    'application/json': {
                        schema: {
                            type: 'object',
                            properties: {
                                success: { type: 'boolean' },
                                error: { type: 'string' }
                            },
                            required: ['success', 'error']
                        }
                    }
                }
            },
            500: {
                description: 'Internal server error',
                content: {
                    'application/json': {
                        schema: {
                            type: 'object',
                            properties: {
                                success: { type: 'boolean' },
                                error: { type: 'string' }
                            },
                            required: ['success', 'error']
                        }
                    }
                }
            }
        }
    },
    'ai.upload-file': {
        description: 'Upload files for use in AI conversations and processing',
        payload: {
            body: {
                type: 'object',
                description: 'File upload request with base64 encoded files',
                properties: {
                    files: {
                        type: 'array',
                        description: 'Array of files to upload. Each file should be base64 encoded.',
                        items: {
                            type: 'object',
                            description: 'Individual file upload data',
                            properties: {
                                name: {
                                    type: 'string',
                                    description: 'Original filename including extension',
                                    example: 'document.pdf'
                                },
                                type: {
                                    type: 'string',
                                    description: 'MIME type of the file',
                                    example: 'application/pdf'
                                },
                                base64: {
                                    type: 'string',
                                    description: 'Base64 encoded file content (without data: prefix)',
                                    example: 'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=='
                                }
                            },
                            required: ['name', 'type', 'base64']
                        },
                        minItems: 1
                    }
                },
                required: ['files']
            }
        },
        responses: {
            200: {
                description: 'Files uploaded successfully',
                content: {
                    'application/json': {
                        schema: {
                            type: 'object',
                            properties: {
                                success: { type: 'boolean' },
                                files: {
                                    type: 'array',
                                    items: {
                                        type: 'object',
                                        properties: {
                                            id: { type: 'number' },
                                            originalName: { type: 'string' },
                                            mimeType: { type: 'string' },
                                            size: { type: 'number' },
                                            url: { type: 'string' }
                                        }
                                    }
                                }
                            },
                            required: ['success', 'files']
                        }
                    }
                }
            },
            400: {
                description: 'Bad request - invalid file data',
                content: {
                    'application/json': {
                        schema: {
                            type: 'object',
                            properties: {
                                success: { type: 'boolean' },
                                files: { type: 'array' },
                                error: { type: 'string' }
                            },
                            required: ['success', 'error']
                        }
                    }
                }
            },
            500: {
                description: 'Upload failed',
                content: {
                    'application/json': {
                        schema: {
                            type: 'object',
                            properties: {
                                success: { type: 'boolean' },
                                files: { type: 'array' },
                                error: { type: 'string' }
                            },
                            required: ['success', 'error']
                        }
                    }
                }
            }
        }
    },
    'ai.create-slot-data': {
        description: 'Create custom prompt slot data for advanced prompt customization',
        payload: {
            body: {
                type: 'object',
                description: 'Slot data creation parameters',
                properties: {
                    label: {
                        type: 'string',
                        description: 'Human-readable label for the slot data',
                        example: 'Target Audience'
                    },
                    value: {
                        type: 'string',
                        description: 'Content value for the slot data',
                        example: 'Software developers with 3-5 years experience'
                    },
                    type: {
                        type: 'string',
                        enum: ['target', 'goal'],
                        description: 'Type of slot data: "target" for audience/context definitions, "goal" for objective specifications'
                    }
                },
                required: ['label', 'value', 'type']
            }
        },
        responses: {
            200: {
                description: 'Slot data created successfully',
                content: {
                    'application/json': {
                        schema: {
                            type: 'object',
                            properties: {
                                success: { type: 'boolean' },
                                data: {
                                    type: 'object',
                                    properties: {
                                        id: { type: 'number' },
                                        label: { type: 'string' },
                                        value: { type: 'string' },
                                        type: { type: 'string' }
                                    }
                                }
                            },
                            required: ['success', 'data']
                        }
                    }
                }
            },
            400: {
                description: 'Bad request - validation error',
                content: {
                    'application/json': {
                        schema: {
                            type: 'object',
                            properties: {
                                success: { type: 'boolean' },
                                error: { type: 'string' }
                            },
                            required: ['success', 'error']
                        }
                    }
                }
            },
            500: {
                description: 'Internal server error',
                content: {
                    'application/json': {
                        schema: {
                            type: 'object',
                            properties: {
                                success: { type: 'boolean' },
                                error: { type: 'string' }
                            },
                            required: ['success', 'error']
                        }
                    }
                }
            }
        }
    },
    'ai.extract-fields': {
        description: 'Extract structured data fields from documents using AI',
        payload: {
            body: {
                type: 'object',
                description: 'Data extraction request parameters',
                properties: {
                    xxx: {
                        type: 'array',
                        items: { type: 'string' },
                        description: 'Array of field names or extraction patterns to identify in the document'
                    },
                    data: {
                        type: 'string',
                        description: 'Document content or text data to extract fields from. Can be plain text, markdown, or structured content.'
                    }
                },
                required: ['data']
            }
        },
        responses: {
            200: {
                description: 'Field extraction successful',
                content: {
                    'application/json': {
                        schema: {
                            type: 'object',
                            properties: {
                                success: { type: 'boolean' },
                                data: {
                                    type: 'object',
                                    description: 'Extracted fields as key-value pairs',
                                    additionalProperties: true
                                }
                            },
                            required: ['success', 'data']
                        }
                    }
                }
            },
            400: {
                description: 'Bad request - invalid extraction parameters',
                content: {
                    'application/json': {
                        schema: {
                            type: 'object',
                            properties: {
                                success: { type: 'boolean' },
                                error: { type: 'string' }
                            },
                            required: ['success', 'error']
                        }
                    }
                }
            },
            500: {
                description: 'Field extraction failed',
                content: {
                    'application/json': {
                        schema: {
                            type: 'object',
                            properties: {
                                success: { type: 'boolean' },
                                error: { type: 'string' }
                            },
                            required: ['success', 'error']
                        }
                    }
                }
            }
        }
    },
    'ai.scrape-web-for-onboarding-step': {
        description: 'Scrape website content and extract data to fill all questions across all steps in a profession for onboarding using AI',
        payload: {
            body: {
                type: 'object',
                properties: {
                    url: {
                        type: 'string',
                        format: 'uri',
                        description: 'Website URL to scrape content from',
                        example: 'https://example.com/about'
                    },
                    profession_id: {
                        type: 'integer',
                        description: 'Onboarding step ID containing questions to fill',
                        example: 123
                    }
                },
                required: ['url', 'profession_id'],
                example: {
                    url: 'https://example.com/about',
                    profession_id: 123
                }
            }
        },
        responses: {
            200: {
                description: 'Successful scraping and data extraction',
                content: {
                    'application/json': {
                        schema: {
                            type: 'object',
                            properties: {
                                success: {
                                    type: 'boolean',
                                    example: true
                                },
                                data: {
                                    type: 'object',
                                    properties: {
                                        stepData: {
                                            type: 'object',
                                            description: 'Extracted data organized by step ID, then by question ID',
                                            additionalProperties: {
                                                type: 'object',
                                                description: 'Questions and answers for this step',
                                                additionalProperties: {
                                                    oneOf: [
                                                        { type: 'string' },
                                                        { type: 'array', items: { type: 'string' } },
                                                        { type: 'null' }
                                                    ]
                                                }
                                            },
                                            example: {
                                                "101": {
                                                    "1": "Extracted company name",
                                                    "2": "john@example.com"
                                                },
                                                "102": {
                                                    "3": ["option1", "option2"],
                                                    "4": null
                                                }
                                            }
                                        }
                                    },
                                    required: ['stepData']
                                }
                            },
                            required: ['success', 'data']
                        }
                    }
                }
            },
            500: {
                description: 'Scraping failed - could be due to invalid URL, profession not found, missing steps/questions, or AI processing error',
                content: {
                    'application/json': {
                        schema: {
                            type: 'object',
                            properties: {
                                success: {
                                    type: 'boolean',
                                    example: false
                                },
                                data: {
                                    type: 'null',
                                    example: null
                                },
                                error: {
                                    type: 'string',
                                    description: 'Human-readable error message'
                                },
                                error_code: {
                                    type: 'integer',
                                    description: 'AI_SCRAPING_STEP_ERROR error code',
                                    example: 5020
                                }
                            },
                            required: ['success', 'data', 'error', 'error_code']
                        }
                    }
                }
            }
        }
    }
};
